// src/components/dashboard/CategoryGrid.tsx
import React from 'react';
import { CategoryShimmer } from '@/components/ui/CategoryShimmer';

interface Category {
  id: string;
  category_name: string;
  status: string;
  metrics_count: number;
  insight_summary?: string;
}

interface CategoryGridProps {
  categories: Category[];
  onCategoryClick: (categoryName: string) => void;
  processingCategories?: Set<string>;
}

export const CategoryGrid: React.FC<CategoryGridProps> = ({
  categories,
  onCategoryClick,
  processingCategories = new Set()
}) => {
  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'optimal':
      case 'normal':
        return '#10B981';
      case 'needs_attention':
      case 'attention':
        return '#F59E0B';
      case 'critical':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  const getCategoryAbbreviation = (categoryName: string) => {
    // Create abbreviations from category names - First letter uppercase, rest lowercase
    const words = categoryName.split(' ');
    let abbreviation = '';

    if (words.length === 1) {
      abbreviation = categoryName.substring(0, 2).toUpperCase();
    } else {
      abbreviation = words.map(w => w[0]).join('').toUpperCase();
    }

    // Format: First letter uppercase, rest lowercase (e.g., "CBC" -> "Cb")
    if (abbreviation.length > 0) {
      return abbreviation.charAt(0).toUpperCase() + abbreviation.slice(1).toLowerCase();
    }

    return abbreviation;
  };

  if (categories.length === 0) {
    return (
      <div style={{
        padding: 'var(--space-12)',
        textAlign: 'center',
        backgroundColor: 'var(--color-surface)',
        borderRadius: 'var(--radius)',
        border: '1px solid var(--color-border)'
      }}>
        <p style={{
          fontFamily: 'var(--font-primary)',
          fontSize: 'var(--text-base)',
          color: 'var(--color-muted)',
          margin: 0
        }}>
          No health categories available. Upload a health report to get started.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
      gap: 'var(--space-3)'
    }}>
      {/* Show shimmer for processing categories */}
      {Array.from(processingCategories).map((categoryName) => (
        <CategoryShimmer key={`processing-${categoryName}`} categoryName={categoryName} />
      ))}

      {/* Show actual categories */}
      {Array.isArray(categories) && categories.map((category) => (
        <button
          key={category.id}
          onClick={() => onCategoryClick(category.category_name)}
          className="category-card"
          style={{
            backgroundColor: 'var(--color-card-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: '8px',
            padding: 'var(--space-4)',
            cursor: 'pointer',
            textAlign: 'center',
            transition: 'all 0.2s ease',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            aspectRatio: '1',
            minHeight: '160px'
          }}
        >
          {/* Status Indicator - Top Right */}
          <div style={{
            position: 'absolute',
            top: 'var(--space-2)',
            right: 'var(--space-2)',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: getStatusColor(category.status),
            boxShadow: `0 0 6px ${getStatusColor(category.status)}40`
          }} />

          {/* Periodic Table Style Layout */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            {/* Category Abbreviation - Periodic Table Style */}
            <div style={{
              fontFamily: 'var(--font-primary)',
              fontSize: '52px',
              fontWeight: '700',
              color: 'var(--color-text)',
              lineHeight: '1',
              letterSpacing: '0px'
            }}>
              {getCategoryAbbreviation(category.category_name)}
            </div>

            {/* Category Name - Below Abbreviation */}
            <div style={{
              fontFamily: 'var(--font-primary)',
              fontSize: '10px',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--color-text)',
              letterSpacing: '0.8px',
              textTransform: 'uppercase',
              marginTop: 'var(--space-1)'
            }}>
              {category.category_name}
            </div>

            {/* Metrics Count */}
            <div style={{
              fontFamily: 'var(--font-primary)',
              fontSize: '10px',
              color: 'var(--color-muted)',
              letterSpacing: '0.3px'
            }}>
              {category.metrics_count} {category.metrics_count === 1 ? 'metric' : 'metrics'}
            </div>
          </div>
        </button>
      ))}

      {/* Hover styles */}
      <style>{`
        .category-card:hover {
          border-color: var(--color-secondary) !important;
          transform: translateY(-3px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
        }

        .category-card:active {
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
};
